import { useDashboardSort } from "@/hooks/dashboard/useDashboardSort";
import {
  DashboardSearch,
  DashboardSearchNoResults,
} from "@/pages/workspace/components/DashboardSearch";
import { WORKSPACE_LABEL } from "@/config/uiConfig";
import { useEffect, useMemo, useState } from "react";
import { Check, FileDown, LayoutPanelTop, Plus, Trash2, X } from "lucide-react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { SectionLayout } from "@/components/layout/SectionLayout";
import { ListPageLayout } from "@/components/layout/ListPageLayout";
import { LoadErrorMessage } from "@/components/LoadErrorMessage";
import { ListPagination } from "@/components/ListPagination";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import { useClientPages } from "@/hooks/usePagedList";
import { isOwnDashboard } from "@/lib/dashboard/dashboardOrigin";
import {
  DashboardTable,
  DashboardTableSkeleton,
  type DashboardSelection,
} from "@/pages/workspace/components/DashboardTable";
import { Button, ConfirmDialog, EmptyState, InlineMessage } from "@nexus/ui";
import { useDashboardTagFilter } from "@/hooks/dashboard/useDashboardTagFilter";
import { useDeleteDashboard } from "@/hooks/dashboard/useDeleteDashboard";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { useDuplicateDashboard } from "@/hooks/dashboard/useDuplicateDashboard";
import { useUpdateDashboard } from "@/hooks/dashboard/useUpdateDashboard";
import { useAccount, useIsAdmin } from "@/hooks/useAccount";
import { useExportComposition } from "@/pages/admin/configuration/useExportComposition";
import { apiErrorMessage } from "@/pages/admin/useTenantAdmin";
import { ImportFromFileItem } from "./components/ImportFromFileItem";

import { useDashboardsNav } from "@/pages/workspace/dashboardsNav";
import { usePinnedDashboards } from "@/hooks/dashboard/usePinnedDashboards";
import { useAppContext } from "@/contexts/AppContext";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { nextUntitledName } from "@/pages/workspace/newDashboard";
import { blankDashboardConfig } from "@/lib/dashboard/contextChoices";
import { clearDashboardDraft, getDashboardDraftKey, writeDashboardDraft } from "./dashboardDraft";
import DashboardTemplatesPage from "./DashboardTemplatesPage";
import { DashboardResourceTabs } from "./components/DashboardResourceTabs";
import {
  canBulkDelete,
  canBulkExport,
  useBulkDashboardActions,
  type BulkOutcome,
} from "./useBulkDashboardActions";

function plural(count: number, one: string, many = `${one}s`) {
  return `${count} ${count === 1 ? one : many}`;
}

/** "3 dashboards deleted.", with the ones that failed and why. */
function BulkOutcomeMessage({ outcome, verb }: { outcome: BulkOutcome; verb: string }) {
  if (outcome.failed.length === 0)
    return (
      <div role="status">
        <InlineMessage
          tone="success"
          title={`${plural(outcome.done.length, "dashboard")} ${verb}.`}
        >
          {null}
        </InlineMessage>
      </div>
    );
  return (
    <InlineMessage
      tone={outcome.done.length ? "warning" : "error"}
      title={`${plural(outcome.done.length, "dashboard")} ${verb}, ${outcome.failed.length} failed.`}
    >
      <ul className="list-inside list-disc space-y-1 wrap-break-word">
        {outcome.failed.map(({ dashboard, reason }) => (
          <li key={dashboard.id}>
            {dashboard.draft?.name ?? dashboard.name}: {reason}
          </li>
        ))}
      </ul>
    </InlineMessage>
  );
}

/** Query parameter asking the list to create a dashboard (e.g. from Home). */
export const CREATE_PARAM = "create";

export default function DashboardListPage() {
  const [parameters] = useSearchParams();
  const authorization = useAuthorization();
  if (
    parameters.get("view") === "templates" &&
    (authorization.isClientProfile || !authorization.canAccessSection("dashboards"))
  )
    return <Navigate to="/workspace/dashboards" replace />;
  return parameters.get("view") === "templates" ? (
    <DashboardTemplatesPage />
  ) : (
    <DashboardDashboardList />
  );
}

function DashboardDashboardList() {
  const navigate = useNavigate();
  const authorization = useAuthorization();
  const canAuthor = !authorization.isClientProfile && authorization.canAccessSection("dashboards");
  const demoEnabled = useAccount().data?.demo_enabled === true;
  // Moving dashboards between platforms goes through the admin-only transfer API.
  const isAdmin = useIsAdmin();
  const exporting = useExportComposition();
  const visibilityUpdate = useUpdateDashboard();
  const { sort, order, onSort: handleSort } = useDashboardSort("updated_at", "desc");
  // `?create=1` (e.g. from Home) creates a dashboard, then leaves the address clean.
  const [createPending, setCreatePending] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const createRequested = searchParams.get(CREATE_PARAM) === "1";
  useEffect(() => {
    if (!createRequested) return;
    if (canAuthor) setCreatePending(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(CREATE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [createRequested, setSearchParams, canAuthor]);
  const { duplicate, isDuplicating, isDuplicateError } = useDuplicateDashboard();
  // One searchable list for both the dashboards provided to the account and
  // the ones its users built.
  const { data, isLoading, isError, refetch } = useDashboards({
    relationship: "accessible",
    state: "all",
    sort,
    order,
  });
  const dashboards = useMemo(
    () =>
      (data ?? []).filter(
        // Someone else's unpublished draft is not something to show.
        (dashboard) => isOwnDashboard(dashboard) || dashboard.state !== "draft",
      ),
    [data],
  );
  const tagFilter = useDashboardTagFilter(dashboards, {
    status: true,
    ownership: true,
    persistKey: "workspace-dashboards",
  });
  const sidebarItems = useDashboardsNav();
  const pins = usePinnedDashboards();
  // Pinned dashboards lead the list, in the table's own order, and follow the
  // search and filters like every other row: a filter never hides the rest behind them.
  const results = useMemo(() => {
    const pinned = new Set(pins.ids);
    const isPinned = (dashboard: DashboardRecord) =>
      dashboard.state !== "draft" && pinned.has(dashboard.id);
    return [
      ...tagFilter.filtered.filter(isPinned),
      ...tagFilter.filtered.filter((dashboard) => !isPinned(dashboard)),
    ];
  }, [tagFilter.filtered, pins.ids]);
  const filterKey = JSON.stringify([tagFilter.search, searchParams.toString(), sort, order]);
  const pages = useClientPages(results, filterKey);
  // Several dashboards at once: checked across pages, cleared when the search changes.
  const bulk = useBulkDashboardActions();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState<DashboardRecord[] | null>(null);
  const [outcome, setOutcome] = useState<{ verb: string; result: BulkOutcome } | null>(null);
  useEffect(() => setSelectedIds(new Set()), [filterKey]);
  const selected = results.filter((dashboard) => selectedIds.has(dashboard.id));
  const deletable = selected.filter(canBulkDelete);
  const exportable = isAdmin ? selected.filter(canBulkExport) : [];
  const bulkBusy = bulk.remove.isPending || bulk.exportAll.isPending;
  const selectable = canAuthor || isAdmin;
  const selection: DashboardSelection | undefined = selectable
    ? {
        isSelected: (dashboard) => selectedIds.has(dashboard.id),
        onToggle: (dashboard, checked) =>
          setSelectedIds((previous) => {
            const next = new Set(previous);
            if (checked) next.add(dashboard.id);
            else next.delete(dashboard.id);
            return next;
          }),
        allSelected: results.length > 0 && selected.length === results.length,
        someSelected: selected.length > 0,
        onToggleAll: (checked) =>
          setSelectedIds(new Set(checked ? results.map((dashboard) => dashboard.id) : [])),
        disabled: bulkBusy,
      }
    : undefined;
  const finish = (verb: string) => (result: BulkOutcome) => {
    setOutcome({ verb, result });
    // Failed ones stay checked, ready for another attempt.
    setSelectedIds(new Set(result.failed.map(({ dashboard }) => dashboard.id)));
  };
  const exportSelected = () => {
    setOutcome(null);
    bulk.exportAll.mutate(exportable, { onSuccess: finish("exported") });
  };
  const {
    mutate: deleteDashboard,
    isPending: isDeleting,
    isError: isDeleteError,
    reset: resetDelete,
  } = useDeleteDashboard();

  const [deleteTarget, setDeleteTarget] = useState<DashboardRecord | null>(null);

  function handleDashboardClick(slug: string) {
    navigate(`/workspace/dashboards/${encodeURIComponent(slug)}`);
  }

  function requestDelete(dashboard: DashboardRecord) {
    if (dashboard.protected) return;
    resetDelete();
    setDeleteTarget(dashboard);
  }

  const [opening, setOpening] = useState(false);
  const { selectedTenantIds } = useAppContext();

  /**
   * Creating a dashboard asks nothing: the editor opens at once on its Context
   * space, where the name, purpose and data scope are the first things on the
   * page. The record is created by the editor's first save, so the list never
   * shows a dashboard that is not open yet.
   */
  function handleCreateDashboard() {
    if (!canAuthor || opening) return;
    const name = nextUntitledName(
      dashboards.map((dashboard) => dashboard.draft?.name ?? dashboard.name),
    );
    const key = getDashboardDraftKey(selectedTenantIds);
    clearDashboardDraft(key);
    writeDashboardDraft(key, [], { name, description: "" }, blankDashboardConfig());
    setOpening(true);
    navigate("/workspace/dashboards/new");
  }

  // Once the list is known, so the new name does not repeat an existing one.
  useEffect(() => {
    if (!createPending || isLoading) return;
    setCreatePending(false);
    handleCreateDashboard();
  });

  function handleDuplicate(dashboard: DashboardRecord) {
    if (!canAuthor || opening || isDuplicating) return;
    void duplicate(dashboard);
  }

  const pageActions = canAuthor ? (
    <Button variant="primary" onClick={handleCreateDashboard} disabled={opening}>
      <Plus className="h-4 w-4" />
      Create dashboard
    </Button>
  ) : undefined;

  return (
    <>
      <SectionLayout
        sectionTitle={WORKSPACE_LABEL}
        sidebarItems={sidebarItems}
        title="Dashboards"
        breadcrumbTrail={[
          { label: WORKSPACE_LABEL, href: "/workspace/dashboards" },
          { label: "Dashboards" },
        ]}
        description="Dashboards you own and those shared with you."
        actions={dashboards.length > 0 ? pageActions : undefined}
        menuItems={isAdmin ? <ImportFromFileItem from="dashboards" /> : undefined}
      >
        {canAuthor && !authorization.isClientProfile && <DashboardResourceTabs kind="dashboard" />}
        <ListPageLayout
          toolbar={
            !isLoading && !isError && dashboards.length > 0 ? (
              <DashboardSearch filter={tagFilter} />
            ) : undefined
          }
        >
          {isError && (
            <LoadErrorMessage
              title="Dashboards could not be loaded"
              onRetry={() => void refetch()}
            />
          )}

          {isLoading && !isError && <DashboardTableSkeleton showOwner />}

          {isDuplicateError && (
            <InlineMessage tone="error" title="Dashboard could not be duplicated">
              Please try again.
            </InlineMessage>
          )}

          {exporting.isError && (
            <InlineMessage tone="error" title="Dashboard could not be exported">
              {apiErrorMessage(exporting.error)}
            </InlineMessage>
          )}

          {visibilityUpdate.isError && (
            <InlineMessage tone="error" title="Dashboard visibility could not be updated">
              Please try again.
            </InlineMessage>
          )}

          {!isLoading && !isError && dashboards.length === 0 && (
            <EmptyState
              icon={LayoutPanelTop}
              title="No dashboards yet"
              description="Dashboards shared with you will appear here."
              action={
                canAuthor ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCreateDashboard}
                    disabled={opening}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create dashboard
                  </Button>
                ) : undefined
              }
            />
          )}

          {!isLoading && !isError && dashboards.length > 0 && (
            <DashboardSearchNoResults filter={tagFilter} />
          )}
          {/* The Widgets bulk bar, as is: select all matching, the count, then the actions. */}
          {selection && !isLoading && !isError && results.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" aria-label="Dashboard bulk actions">
              <Button
                size="sm"
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => selection.onToggleAll(!selection.allSelected)}
              >
                {selection.allSelected ? (
                  <X aria-hidden className="size-4" />
                ) : (
                  <Check aria-hidden className="size-4" />
                )}
                {selection.allSelected ? "Clear selection" : "Select all matching"}
              </Button>
              <span role="status" className="text-sm text-secondary">
                {selected.length} selected
              </span>
              {isAdmin ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={bulkBusy || exportable.length === 0}
                  title={
                    selected.length > exportable.length
                      ? "Unpublished drafts are not exported"
                      : "One file per dashboard"
                  }
                  onClick={exportSelected}
                >
                  <FileDown aria-hidden className="size-4" />
                  {bulk.exportAll.isPending ? "Exporting..." : "Export selected"}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkBusy || deletable.length === 0}
                title={
                  selected.length > deletable.length
                    ? "Protected dashboards and those you cannot change are kept"
                    : undefined
                }
                onClick={() => {
                  setOutcome(null);
                  bulk.remove.reset();
                  setBulkDeleting(deletable);
                }}
              >
                <Trash2 aria-hidden className="size-4" />
                {bulk.remove.isPending ? "Deleting..." : "Delete selected"}
              </Button>
            </div>
          )}
          {outcome ? <BulkOutcomeMessage outcome={outcome.result} verb={outcome.verb} /> : null}
          {!isLoading && !isError && results.length > 0 && (
            <DashboardTable
              presentation="catalog"
              selection={selection}
              showOwner
              dashboards={pages.rows}
              sort={sort}
              order={order}
              onSort={handleSort}
              pinnable
              onOpen={(dashboard) => handleDashboardClick(dashboard.slug)}
              getOpenHref={(dashboard) =>
                `/workspace/dashboards/${encodeURIComponent(dashboard.slug)}`
              }
              onEdit={
                canAuthor
                  ? (dashboard) => {
                      if (!dashboard.protected)
                        navigate(`/workspace/dashboards/${dashboard.id}/edit`);
                    }
                  : undefined
              }
              onDelete={requestDelete}
              onDuplicate={canAuthor ? handleDuplicate : undefined}
              onExport={
                isAdmin
                  ? (dashboard) =>
                      exporting.mutate({
                        kind: "dashboards",
                        id: dashboard.id,
                        slug: dashboard.slug,
                      })
                  : undefined
              }
              onToggleDemoVisibility={
                authorization.profile === "nagra-admin" && demoEnabled
                  ? (dashboard) => {
                      if (visibilityUpdate.isPending) return;
                      visibilityUpdate.mutate({
                        id: dashboard.id,
                        status: dashboard.status === "active" ? "inactive" : "active",
                      });
                    }
                  : undefined
              }
            />
          )}
          {!isLoading && !isError && dashboards.length > 0 && (
            <ListPagination label="Dashboard pages" pages={pages} />
          )}
        </ListPageLayout>
      </SectionLayout>

      <ConfirmDialog
        open={bulkDeleting !== null}
        title={`Delete ${plural(bulkDeleting?.length ?? 0, "dashboard")}?`}
        description={`These dashboards and their saved layouts will be removed. This cannot be undone.${
          selected.length > (bulkDeleting?.length ?? 0)
            ? ` ${plural(selected.length - (bulkDeleting?.length ?? 0), "selected dashboard")} protected or not yours to change will be kept.`
            : ""
        }`}
        confirmLabel={bulk.remove.isPending ? "Deleting…" : "Delete dashboards"}
        cancelLabel="Cancel"
        tone="danger"
        pending={bulk.remove.isPending}
        onClose={() => setBulkDeleting(null)}
        onConfirm={() => {
          if (!bulkDeleting || bulk.remove.isPending) return;
          bulk.remove.mutate(bulkDeleting, {
            onSuccess: finish("deleted"),
            onSettled: () => setBulkDeleting(null),
          });
        }}
      >
        <ul className="max-h-48 space-y-1 overflow-y-auto text-sm text-secondary">
          {bulkDeleting?.map((dashboard) => (
            <li key={dashboard.id} className="wrap-break-word">
              {dashboard.draft?.name ?? dashboard.name}
            </li>
          ))}
        </ul>
      </ConfirmDialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete dashboard"
        description={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This removes the dashboard and all of its saved layout. This cannot be undone.`
            : ""
        }
        confirmLabel={isDeleting ? "Deleting…" : "Delete dashboard"}
        cancelLabel="Cancel"
        tone="danger"
        pending={isDeleting}
        onClose={() => {
          resetDelete();
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteDashboard(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          });
        }}
      >
        {isDeleteError ? (
          <InlineMessage tone="error" title="Dashboard could not be deleted">
            Check your connection and try again.
          </InlineMessage>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
